import {DB_COLLECTION_NAMES, MESSAGES, PAGE_URLS, SETTING_SPEED_UNITS} from "constants/AppConstants.js";
import DateTimeUtil from "utils/DateTimeUtil.js";
import ThirdPartyIdUtil from "utils/ThirdPartyIdUtil.js";
import {Result} from "wrappers/Result.js";
import LocalStorageService from "services/LocalStorageService.js";
import LogService from "services/LogService.js";
import CoordinateUtil from "utils/CoordinateUtil.js";
import {calculateBestAvgSpeed, convertPointToPointDb, getPausePoints} from "services/ActivityService.js";
import {
  child,
  endAt,
  equalTo,
  get,
  limitToFirst,
  orderByChild,
  orderByKey,
  push,
  query,
  remove,
  set,
  startAfter,
  startAt,
  update
} from "firebase/database";
import {LocalDbService} from "services/LocalDBService.js";
import {
  getBytes,
  ref as storageRef,
  uploadBytes,
  uploadString,
  getDownloadURL,
  deleteObject,
  listAll
} from "firebase/storage";
import type {QueryConstraint} from "firebase/database";
import type {SearchFilter, StorageFileEntry, SubscriptionStatus, UidAndUser} from "./FirebaseDbService.types.js";

export type {SearchFilter, StorageFileEntry, SubscriptionStatus, UidAndUser} from "./FirebaseDbService.types.js";

let auth: any;
let dbRef: any;
let isAndroid = false;
let storage: any;
let initError: any = null;

let markFirebaseReady: () => void = () => {};
const firebaseReady = new Promise<void>((resolve) => {
  markFirebaseReady = resolve;
});

/**
 * The init below can't use top-level await (it would break require() from the
 * functions runtime), so every binding in this module is unset until its IIFE
 * finishes — and `isAndroid` defaults to a plausible-looking false, so an early
 * read silently takes the web path on Android.
 *
 * Awaited once per entry point rather than per call: src/index.tsx before render,
 * createCloudFunction before invoking a handler. Resolves even if init failed, so
 * a broken config surfaces as a failed call rather than a hang.
 */
export const awaitFirebaseReady = (): Promise<void> => firebaseReady;

let getFromStorage: (filePath: string) => Promise<any>;
let writeToStorage: (filePath: string, data: any) => Promise<any>;
let writeStringToStorage: (filePath: string, data: any) => Promise<any>;
let deleteFromStorage: (filePath: string) => Promise<any>;
let getDownloadURLFromStorage: (filePath: string) => Promise<any>;
let listFilesFromStorage: (dirPath: string, maxResults?: number) => Promise<StorageFileEntry[]>;
let listActivityPhotosFromStorage: (dirPath: string, maxSlots?: number) => Promise<StorageFileEntry[]>;
let logger: any;

const normalizeStoragePath = (filePath: string): string => (filePath || "").replace(/^\/+/, "");

const getStorageFileBaseName = (file: any): string => {
  const shortName = file?.name?.trim();
  if (shortName && !shortName.includes("/")) {
    return shortName;
  }
  const path = file?.path?.trim();
  if (path) {
    return path.split("/").pop() || "";
  }
  return shortName || "";
};

const isExcludedActivityPhotoFileName = (fileName: string): boolean => {
  const baseName = getStorageFileBaseName({name: fileName});
  return /track/i.test(baseName);
};
let isFromFunctions = false;
let databaseURL = "https://waterspeedapp.firebaseio.com";
// Web vs Cloud Functions storage/init wiring. The if/else MUST stay at module
// top level: on the web build webpack (via DefinePlugin) evaluates the condition
// to a constant and dead-code-eliminates the unused branch — that is what keeps
// the browser bundle from ever resolving the server-only "../../firebaseUtils.js"
// (and firebase-admin's Node built-ins) below. The awaits live inside a
// per-branch fire-and-forget IIFE (never top-level await) so the functions
// runtime can still require() this module without hitting
// ERR_REQUIRE_ASYNC_MODULE. Bindings are only read at request time.
if (typeof process.env.REACT_APP_FIREBASE_API_KEY !== "undefined") {
  void (async () => {
    logger = console;
    // @ts-ignore -- web-bundler-resolved path, not visible to the Node TS program
    const module = await import("/src/FirebaseUtils.js");
    // @ts-ignore -- web-bundler-resolved alias, not visible to the Node TS program
    const {firebaseConfig} = await import("configs/firebaseConfig.js");
    databaseURL = firebaseConfig.databaseURL;
    auth = module.auth;
    dbRef = module.dbRef;
    isAndroid = module.isAndroid;
    storage = module.storage;
    getDownloadURLFromStorage = async (filePath) => {
      const path = normalizeStoragePath(filePath);
      if (isAndroid) {
        const result = await storage.getDownloadUrl({path});
        return result.downloadUrl;
      } else {
        const fileRef = storageRef(storage, path);
        return getDownloadURL(fileRef);
      }
    };
    deleteFromStorage = async (filePath) => {
      const path = normalizeStoragePath(filePath);
      if (isAndroid) {
        await storage.delete({path});
      } else {
        const fileRef = storageRef(storage, path);
        await deleteObject(fileRef);
      }
    };
    listFilesFromStorage = async (dirPath, maxResults = 100) => {
      const normalizedPath = dirPath.replace(/\/$/, "");
      if (isAndroid) {
        const collected = [];
        let pageToken;
        do {
          const result = await storage.listFiles({
            path: normalizedPath,
            maxResults,
            pageToken
          });
          collected.push(...(result.items || []));
          pageToken = result.nextPageToken;
        } while (pageToken && collected.length < maxResults);
        return collected.map((item) => ({
          name: getStorageFileBaseName(item),
          path: normalizeStoragePath(item.path)
        }));
      }
      const listRef = storageRef(storage, normalizedPath);
      const result = await listAll(listRef);
      return result.items.map((item) => ({
        name: item.name,
        path: normalizeStoragePath(item.fullPath)
      }));
    };
    listActivityPhotosFromStorage = async (dirPath, maxSlots = 5) => {
      const normalizedPath = normalizeStoragePath(dirPath.replace(/\/$/, ""));
      const isPhotoFile = (fileName) => /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(fileName || "");

      let listedFiles = [];
      try {
        listedFiles = await listFilesFromStorage(normalizedPath);
      } catch (error) {
        console.warn("listFilesFromStorage failed for activity photos:", error);
      }

      const photos = listedFiles
        .map((file) => {
          const name = getStorageFileBaseName(file);
          if (!name) {
            return null;
          }
          return {
            name,
            path: normalizeStoragePath(file.path || `${normalizedPath}/${name}`)
          };
        })
        .filter((file) => file && isPhotoFile(file.name) && !isExcludedActivityPhotoFileName(file.name));

      if (!isAndroid || photos.length > 0) {
        return photos;
      }

      const probed = [];
      for (let slot = 1; slot <= maxSlots; slot += 1) {
        const name = `${slot}.jpg`;
        const path = `${normalizedPath}/${name}`;
        try {
          await getDownloadURLFromStorage(path);
          probed.push({name, path});
        } catch (_error) {
          // Slot empty or unreadable — skip.
        }
      }
      return probed;
    };
    getFromStorage = async (filePath) => {
      if (isAndroid) {
        const result = await getDownloadURLFromStorage(filePath);
        const response = await fetch(result);
        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
      } else {
        // Use web Firebase Storage SDK
        const fileRef = storageRef(storage, filePath);
        return await getBytes(fileRef);
      }
    };
    writeToStorage = async (filePath, data) => {
      if (isAndroid) {
        const {Filesystem, Directory, Encoding} = await import("@capacitor/filesystem");

        const fileExtension = filePath.includes(".") ? filePath.substring(filePath.lastIndexOf(".")) : "";
        const tempFileName = `temp_${Date.now()}${fileExtension}`;

        // Convert File object to base64 string for Capacitor
        let fileData;
        let encoding;

        if (data instanceof Blob) {
          // Convert Blob/File to base64 string for writing to filesystem
          const arrayBuffer = await data.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const binaryString = Array.from(uint8Array, (byte) => String.fromCharCode(byte)).join("");
          fileData = btoa(binaryString);
          encoding = undefined;
        } else if (typeof data === "string") {
          fileData = data;
          encoding = Encoding.UTF8;
        } else {
          // For other data types, try to convert to string
          fileData = String(data);
          encoding = Encoding.UTF8;
        }

        // Write data to temporary file in cache directory
        const writeResult = await Filesystem.writeFile({
          path: tempFileName,
          data: fileData,
          directory: Directory.Cache,
          encoding: encoding
        });

        try {
          // Upload using the Capacitor Firebase Storage plugin
          const result = await storage.uploadFile({
            path: filePath, // Firebase storage path (destination)
            uri: writeResult.uri // Local temp file URI (source)
          });

          // Clean up temp file
          await Filesystem.deleteFile({
            path: tempFileName,
            directory: Directory.Cache
          }).catch(() => {}); // Ignore cleanup errors

          return result;
        } catch (error) {
          // Clean up temp file on error
          await Filesystem.deleteFile({
            path: tempFileName,
            directory: Directory.Cache
          }).catch(() => {}); // Ignore cleanup errors

          throw error;
        }
      } else {
        // Use web Firebase Storage SDK
        const fileRef = storageRef(storage, filePath);
        if (typeof data === "string") {
          // For string data, use uploadString
          return await uploadString(fileRef, data, "raw");
        }
        if (data instanceof Blob) {
          // Preserve content type for files/blobs
          return await uploadBytes(fileRef, data);
        }
        // Fallback for non-string data
        const blob = new Blob([data], {type: "application/json"});
        return await uploadBytes(fileRef, blob);
      }
    };
    writeStringToStorage = async (filePath, data) => {
      // Convert data to string and use writeToStorage
      const dataString = typeof data === "string" ? data : JSON.stringify(data);
      return await writeToStorage(filePath, dataString);
    };
    // finally(), not a call here: if an import throws, readiness must still resolve
    // or the startup gate in index.tsx waits forever. The rejection stays unhandled
    // on purpose so GlobalErrorHandler still reports it.
  })().finally(markFirebaseReady);
} else {
  void (async () => {
    isFromFunctions = true;
    try {
      // @ts-ignore -- resolved relative to the deployed functions bundle, not this source tree
      const module = await import("../../firebaseUtils.js");
      logger = module.logger;
      dbRef = module.dbRef;
      storage = module.storage;
    } catch (error) {
      initError = error;
      try {
        const module = await import("../functions/firebaseUtils.js");
        logger = module.logger;
        dbRef = module.dbRef;
        storage = module.storage;
        initError = null;
      } catch (fallbackError) {
        console.error(initError);
        console.error(fallbackError);
      }
    }

    getDownloadURLFromStorage = (filePath) => {
      return storage.bucket(process.env.CUSTOM_FIREBASE_STORAGE_BUCKET).file(filePath).getDownloadUrl();
    };
    deleteFromStorage = (filePath) => {
      return storage.bucket(process.env.CUSTOM_FIREBASE_STORAGE_BUCKET).file(filePath).delete();
    };
    listFilesFromStorage = async (dirPath, maxResults = 100) => {
      const normalizedPath = dirPath.replace(/\/$/, "");
      const [files] = await storage.bucket(process.env.CUSTOM_FIREBASE_STORAGE_BUCKET).getFiles({
        prefix: `${normalizedPath}/`,
        maxResults
      });
      return files.map((file) => {
        const path = file.name;
        return {
          name: path.split("/").pop(),
          path
        };
      });
    };
    listActivityPhotosFromStorage = async (dirPath, maxSlots = 5) => {
      const normalizedPath = normalizeStoragePath(dirPath.replace(/\/$/, ""));
      const isPhotoFile = (fileName) => /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(fileName || "");
      const listedFiles = await listFilesFromStorage(normalizedPath);
      return listedFiles
        .map((file) => {
          const name = getStorageFileBaseName(file);
          if (!name) {
            return null;
          }
          return {
            name,
            path: normalizeStoragePath(file.path || `${normalizedPath}/${name}`)
          };
        })
        .filter((file) => file && isPhotoFile(file.name) && !isExcludedActivityPhotoFileName(file.name))
        .slice(0, maxSlots);
    };
    getFromStorage = (filePath) => {
      return storage
        .bucket(process.env.CUSTOM_FIREBASE_STORAGE_BUCKET)
        .file(filePath)
        .download()
        .then((data) => {
          return data[0];
        });
    };
    writeToStorage = (filePath, data) => {
      return storage
        .bucket(process.env.CUSTOM_FIREBASE_STORAGE_BUCKET)
        .file(filePath)
        .save(data, {
          resumable: false,
          metadata: {
            contentType: "application/json"
          }
        });
    };
    writeStringToStorage = (filePath, data) => {
      return writeToStorage(filePath, data);
    };
  })().finally(markFirebaseReady);
}

// Export the storage function for use by other services
export {
  writeStringToStorage,
  deleteFromStorage,
  getDownloadURLFromStorage,
  writeToStorage,
  getFromStorage,
  listFilesFromStorage,
  listActivityPhotosFromStorage
};

let AuthService: any;
if (typeof process.env.REACT_APP_FIREBASE_API_KEY !== "undefined") {
  import("services/AuthService.js").then((module) => {
    AuthService = module.default;
  });
}

async function signOutAndRedirectAsync(uid: string, data: any): Promise<void> {
  const userSubscriptionExpiryDate = DateTimeUtil.getDateByTimeStamp(data.subExpDateInUnixTimestamp);
  LogService.addInfoLogAsync(
    uid,
    `user subscription expired on date: ${userSubscriptionExpiryDate.toISOString()}`,
    false,
    true
  );

  await AuthService.signOutAsync(isAndroid);
  LocalStorageService.clear();

  window.location.href = `${PAGE_URLS.subscriptionExpirePageUrl}?uid=${uid}&date=${data.subExpDateInUnixTimestamp}`;
}

export function isUserPremium(user: any): SubscriptionStatus {
  // Check new structure (timestamp in seconds)
  if (user?.subscription?.premium?.expiration) {
    const currentDate = new Date();
    const expirationDate = user.subscription.premium.expiration; // seconds
    const premiumExpiryDate = new Date(expirationDate * 1000);
    const isActive = premiumExpiryDate.getTime() >= currentDate.getTime();

    return {isActive, expirationDate};
  }

  return {isActive: false, expirationDate: null};
}

export function isUserUltra(user: any): SubscriptionStatus {
  // Check new structure (timestamp in seconds)
  if (user?.subscription?.ultra?.expiration) {
    const currentDate = new Date();
    const expirationDate = user.subscription.ultra.expiration; // seconds
    const ultraExpiryDate = new Date(expirationDate * 1000);
    const isActive = ultraExpiryDate.getTime() >= currentDate.getTime();

    return {isActive, expirationDate};
  }

  return {isActive: false, expirationDate: null};
}

/** The rule `isUserProAsync` applies, without its client-side sign-out, for server code. */
export function hasProSubscription(user: any): boolean {
  if (user?.sandboxUser || isUserUltra(user).isActive || isUserPremium(user).isActive) {
    return true;
  }
  const twentyFourHourInSeconds = 86400;
  return (
    !!user?.subscriptionExpDate &&
    DateTimeUtil.getDateByTimeStamp(user.subscriptionExpDate + twentyFourHourInSeconds).getTime() >= Date.now()
  );
}

export async function isUserProAsync(uid: string): Promise<Result<any>> {
  const userCollection = new UserCollection();

  const user = await userCollection.getUserByIdAsync(uid);
  if (!user) {
    return Result.failure("user is null cannot check for pro subscription");
  }

  const twentyFourHourInSeconds = 86400;
  const currentDate = new Date();

  // Check new subscription structure using helper functions
  const ultraStatus = isUserUltra(user);
  const premiumStatus = isUserPremium(user);

  let hasActiveSubscription = ultraStatus.isActive || premiumStatus.isActive;
  let subscriptionExpDate = ultraStatus.isActive ? ultraStatus.expirationDate : premiumStatus.expirationDate;

  // Fallback to legacy subscription field for backward compatibility
  if (!hasActiveSubscription && user.subscriptionExpDate) {
    subscriptionExpDate = user.subscriptionExpDate;
    const userSubscriptionExpiryDate = DateTimeUtil.getDateByTimeStamp(
      user.subscriptionExpDate + twentyFourHourInSeconds
    );
    hasActiveSubscription = userSubscriptionExpiryDate.getTime() >= currentDate.getTime();
  }

  // Check if subscription is expired (unless sandbox user)
  if (!user.sandboxUser && !hasActiveSubscription) {
    if (typeof process.env.REACT_APP_FIREBASE_API_KEY !== "undefined") {
      if (isAndroid) {
        return Result.failure(`user ${uid} subscription is expired cannot fetch data...`);
      }

      await signOutAndRedirectAsync(uid, {subExpDateInUnixTimestamp: subscriptionExpDate});
    }
    return Result.failure(`user ${uid} subscription is expired cannot fetch data...`);
  }

  return Result.success({isPro: true, subExpDateInUnixTimestamp: subscriptionExpDate});
}

export class FirebaseQuery {
  static async getAsync(uid: string, path: string, skipSubscriptionCheck = false): Promise<Result<any>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    // A failed init leaves dbRef unset while awaitFirebaseReady still resolves;
    // fail loudly instead of letting every read masquerade as "not found"
    if (!dbRef) {
      throw new Error(`Firebase init failed: ${initError?.message ?? "dbRef is not initialized"}`);
    }

    try {
      const snapshot = await get(child(dbRef, path));
      if (snapshot.exists()) {
        return Result.success(snapshot.val());
      } else {
        LogService.addInfoLogAsync(uid, "getAsync path not exists" + path, false, true);
        return Result.failure(`invalid path ${path}`);
      }
    } catch (error) {
      LogService.addErrorLogAsync(uid, "error getAsync " + path + " " + error.message, error.stack);
      return Result.failure(MESSAGES.error);
    }
  }

  static async getWithFiltersAsync(
    uid: string,
    path: string,
    filters: QueryConstraint[],
    skipSubscriptionCheck = false,
    throwError = false
  ): Promise<Result<any>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    if (!dbRef) {
      throw new Error(`Firebase init failed: ${initError?.message ?? "dbRef is not initialized"}`);
    }

    try {
      const snapshot = await get(query(child(dbRef, path), ...filters));
      if (snapshot.exists()) {
        return Result.success(snapshot.val());
      } else {
        return Result.failure(`invalid path ${path}`);
      }
    } catch (error) {
      if (throwError) {
        throw error;
      }
      LogService.addErrorLogAsync(uid, "error getWithFiltertAsync" + error.message, error.stack);
      return Result.failure(MESSAGES.error);
    }
  }

  static async updateAsync(
    uid: string,
    path: string,
    obj: any,
    onCompleteCallback: ((res?: any) => any) | null = () => {},
    skipSubscriptionCheck = false
  ): Promise<Result<any>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    try {
      await update(child(dbRef, path), obj)
        .then(async (res) => {
          await onCompleteCallback?.(res);
        })
        .catch(async (error) => {
          await onCompleteCallback?.(error);
          LogService.addErrorLogAsync(uid, "error updateAsync" + error.message, error.stack);
          return Result.failure(MESSAGES.error);
        });
      return Result.success(obj);
    } catch (error) {
      onCompleteCallback?.(MESSAGES.error);
      LogService.addErrorLogAsync(uid, "error updateAsync 2" + error.message, error.stack);
      return Result.failure(MESSAGES.error);
    }
  }

  static async updateMultiPathAsync(
    obj: any,
    onCompleteCallback: ((res?: any) => any) | null = () => {}
  ): Promise<Result<any>> {
    if (!isFromFunctions) {
      throw new Error("updateMultiPathAsync is not supported in this environment");
    }

    try {
      logger.info("Updating multi path ...", obj);
      await dbRef
        .update(obj)
        .then(async (res) => {
          logger.info("Updated multi path successfully", obj);
          await onCompleteCallback?.(res);
        })
        .catch(async (error) => {
          logger.error("Error updating multi path", error);
          await onCompleteCallback?.(error);
        });
      return Result.success(obj);
    } catch (error) {
      logger.error("Error updating multi path", error);
      onCompleteCallback?.(MESSAGES.error);
      return Result.failure(MESSAGES.error);
    }
  }

  static async setAsync(
    uid: string,
    path: string,
    obj: any,
    onCompleteCallback: ((res?: any) => any) | null = () => {},
    skipSubscriptionCheck = false
  ): Promise<Result<any>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    try {
      await set(child(dbRef, path), obj);
      await onCompleteCallback?.();
      return Result.success(obj);
    } catch (error) {
      await onCompleteCallback?.(error);
      LogService.addErrorLogAsync(
        uid,
        "error setAsync path " + path + " *** error: " + error.message,
        error.stack,
        false,
        true
      );
      return Result.failure(error?.message || MESSAGES.error);
    }
  }

  static async addAsync(uid: string, path: string, obj: any, skipSubscriptionCheck = false): Promise<Result<any>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    try {
      const ref = await push(child(dbRef, path), obj);
      return Result.success(obj, ref.key);
    } catch (error) {
      LogService.addErrorLogAsync(uid, "error addAsync" + error.message, error.stack);
      return Result.failure(MESSAGES.error);
    }
  }

  static async removeAsync(uid: string, path: string, skipSubscriptionCheck = false): Promise<Result<any>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    try {
      await remove(child(dbRef, path));
      return Result.success(true);
    } catch (error) {
      LogService.addErrorLogAsync(uid, "error removeAsync" + error.message, error.stack);
      return Result.failure(error, false);
    }
  }

  /**
   * Efficiently counts the number of child nodes at a given path using the REST API shallow query.
   * This is much more efficient than fetching the full snapshot for large collections.
   * @param {string} uid - The user ID.
   * @param {string} path - The path to the collection.
   * @param {boolean} skipSubscriptionCheck - Whether to skip the subscription check.
   * @param {Object} params - Additional parameters to append to the URL.
   * @return {Promise<Result<number>>} - The result containing the count or an error.
   */
  static async countShallowAsync(
    uid: string,
    path: string,
    skipSubscriptionCheck = false,
    params: Record<string, any> = {}
  ): Promise<Result<number>> {
    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return isUserProResult;
      }
    }

    try {
      if (!auth?.currentUser) {
        return Result.failure("User not authenticated");
      }

      const idToken = await auth.currentUser.getIdToken();
      // Use standard fetch to call the REST API with shallow=true
      const url = `${databaseURL}/${path}.json?shallow=true&auth=${idToken}${Object.keys(params).length ? "&" + new URLSearchParams(params).toString() : ""}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`REST API error: ${response.statusText}`);
      }

      const data = await response.json();
      const count = data ? Object.keys(data).length : 0;

      return Result.success(count);
    } catch (error) {
      LogService.addErrorLogAsync(uid, "error countShallowAsync query: " + error.message, error.stack);
      return Result.failure(MESSAGES.error);
    }
  }

  static createQuery(
    offset: any,
    search: SearchFilter | null = null,
    limit = 30,
    endAtCond = true,
    lastOffsetKey: any = undefined
  ): QueryConstraint[] {
    const filters: QueryConstraint[] = [];
    if (search) {
      if (search.field === "uid") {
        filters.push(orderByKey());
      } else {
        filters.push(orderByChild(search.field));
      }
      if (endAtCond) {
        filters.push(endAt(search.value?.trim() + "\uf8ff"));
      }
      if (!offset) {
        // Check if we can trim the value to search
        let valueToSearch = search.value;
        if (typeof valueToSearch === "string") {
          valueToSearch = valueToSearch?.trim();
        }
        if (lastOffsetKey) {
          filters.push(startAt(valueToSearch, lastOffsetKey));
        } else {
          filters.push(startAt(valueToSearch));
        }
      }
    } else {
      filters.push(orderByKey());
    }
    if (offset) {
      filters.push(startAfter(offset, lastOffsetKey));
      if (limit) {
        filters.push(limitToFirst(limit + 1));
      }
    } else {
      if (limit) {
        filters.push(limitToFirst(limit));
      }
    }

    return filters;
  }
}

// Profile fields owned by Capacitor/web. Must sync to Firebase on change.
// Subscription fields are managed separately and must not be written from profile sync.
export const CAPACITOR_PROFILE_FIELDS = [
  "name",
  "displayName",
  "weight",
  "height",
  "country",
  "dateOfBirth",
  "sports",
  "imgSrc",
  "privacyOptions"
];

export const buildProfilePatch = (userInDb: any): any => {
  const patch: any = {};
  for (const field of CAPACITOR_PROFILE_FIELDS) {
    if (!(field in userInDb)) {
      continue;
    }
    const value = userInDb[field];
    if (value === null || value === undefined) {
      continue;
    }
    if (field === "sports" && Array.isArray(value) && value.length === 0) {
      continue;
    }
    patch[field] = value;
  }
  const resolvedName = userInDb.displayName || userInDb.name;
  if (resolvedName) {
    patch.name = resolvedName;
    patch.displayName = resolvedName;
  }
  const profileLmd = userInDb.profileLmd ?? userInDb.lmd;
  if (profileLmd) {
    patch.profileLmd = profileLmd;
    patch.lmd = profileLmd;
    patch.lmdFull = userInDb.lmdFull;
  }
  return patch;
};

// Profile freshness is tracked separately from global lmd (subscription/login can bump lmd only).
export const getProfileLmd = (userInDb: any): number => {
  if (!userInDb) {
    return 0;
  }
  if (userInDb.profileLmd) {
    return userInDb.profileLmd;
  }
  // Legacy users: fall back to lmd until the first profile save writes profileLmd.
  return userInDb.lmd ?? 0;
};

export const mergeUserWithProfileSource = (
  remoteUserInDb: any,
  profileSourceInDb: any,
  uid: string,
  entitlement: any = null
): any => {
  const merged: any = {
    ...remoteUserInDb,
    ...buildProfilePatch(profileSourceInDb || remoteUserInDb),
    ID: uid
  };
  if (entitlement !== null) {
    merged.entitlement = entitlement;
  }
  return merged;
};

export const isLocalProfileNewer = (localUserInDb: any, remoteUserInDb: any): boolean => {
  if (!remoteUserInDb) {
    return true;
  }
  if (!localUserInDb) {
    return false;
  }
  return getProfileLmd(localUserInDb) >= getProfileLmd(remoteUserInDb);
};

export class UserCollection {
  async syncProfileToFirebaseAsync(uid: string, userInDb: any, skipSubscriptionCheck = true): Promise<any> {
    const profilePatch = buildProfilePatch(userInDb);
    if (Object.keys(profilePatch).length === 0) {
      return;
    }
    const now = Math.floor(Date.now() / 1000);
    if (!profilePatch.profileLmd) {
      profilePatch.profileLmd = profilePatch.lmd ?? now;
    }
    if (!profilePatch.lmd) {
      profilePatch.lmd = profilePatch.profileLmd;
      profilePatch.lmdFull = new Date().toISOString();
    }
    return await this.updateUserAsync(uid, profilePatch, skipSubscriptionCheck);
  }

  async getAllUsers(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    skipSubscriptionCheck = false,
    limit = 50,
    endAtCond = true,
    lastOffsetKey: any = undefined
  ): Promise<any> {
    const filters = FirebaseQuery.createQuery(offset, search, limit, endAtCond, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(
      uid,
      DB_COLLECTION_NAMES.users,
      filters,
      skipSubscriptionCheck
    );
    return response.data;
  }

  async getUserByIdAsync(uid: string, skipSubscriptionCheck = true, forceRemote = false): Promise<any> {
    if (isAndroid && !forceRemote) {
      LogService.addInfoLogAsync("", "getUserByIdAsync started no remote " + uid, false, true);

      const userLocal = await (await LocalDbService.getLocalDbService()).getUser(uid).catch((err) => {
        LogService.addErrorLogAsync("", "Can't get user locally: " + err, false, true);
      });

      LogService.addInfoLogAsync("", "getUserByIdAsync started done", false, true);

      if (userLocal) {
        LogService.addInfoLogAsync("", "getUserByIdAsync return", false, true);
        return userLocal;
      }
    }

    if (isAndroid) {
      LogService.addInfoLogAsync("", "getUserByIdAsync started remote " + uid, false, true);
    }

    const response = await FirebaseQuery.getAsync(uid, `${DB_COLLECTION_NAMES.users}/${uid}`, skipSubscriptionCheck);
    return response.data;
  }

  async updateUserAsync(uid: string, user: any, skipSubscriptionCheck = false): Promise<any> {
    user = {ID: uid, ...user};

    if (isAndroid) {
      await (await LocalDbService.getLocalDbService()).setUser(user).catch((err) => {
        logger.error("Can't set user locally : ", err);
      });

      // Get existing user data to merge properly
      const existingUser = await this.getUserByIdAsync(uid, skipSubscriptionCheck, true);

      // Filter out null values to prevent deletion
      const filteredUser = this.filterNullValues(user);

      // Merge with existing data, preserving non-null values
      const mergedUser = existingUser ? {...existingUser, ...filteredUser} : filteredUser;

      // Subscription stuff are managed by native apps only
      delete mergedUser.subscription;
      delete mergedUser.subscriptionExpDate;
      delete mergedUser.subscriptionExpDateFull;
      delete mergedUser.subscriptionType;
      delete mergedUser.subscriptionStatus;
      delete mergedUser.isFree;

      await FirebaseQuery.updateAsync(
        uid,
        `${DB_COLLECTION_NAMES.users}/${uid}`,
        mergedUser,
        null,
        skipSubscriptionCheck
      );

      await (await LocalDbService.getLocalDbService()).setUser({ID: uid, ...mergedUser}).catch((err) => {
        logger.error("Can't sync merged user locally : ", err);
      });
      return;
    }

    if (!isAndroid) {
      // Separate nested path updates (e.g., "subscription/premium/expiration") from regular fields
      const nestedPathUpdates = {};
      const regularUpdates = {};

      for (const [key, value] of Object.entries(user)) {
        if (key.includes("/")) {
          // This is a nested path update for Firebase (e.g., "subscription/premium/expiration")
          nestedPathUpdates[key] = value;
        } else {
          regularUpdates[key] = value;
        }
      }

      // Handle regular updates with merge logic
      let mergedUser: any = {};
      if (Object.keys(regularUpdates).length > 0) {
        // Get existing user data to merge properly
        const existingUser = await this.getUserByIdAsync(uid, skipSubscriptionCheck, true);

        // Filter out null values to prevent deletion
        const filteredUser = this.filterNullValues(regularUpdates);

        // Merge with existing data, preserving non-null values
        mergedUser = existingUser ? {...existingUser, ...filteredUser} : filteredUser;
      }

      // Always delete subscription fields from mergedUser
      // Subscriptions are managed by Android native app or via nested path updates from cloud functions
      delete mergedUser.subscription;
      delete mergedUser.subscriptionExpDate;
      delete mergedUser.subscriptionExpDateFull;
      delete mergedUser.subscriptionType;
      delete mergedUser.subscriptionStatus;
      delete mergedUser.isFree;

      // Combine regular updates and nested path updates
      // Nested path updates (if any) will be applied by Firebase after the base object
      const finalUpdates =
        Object.keys(nestedPathUpdates).length > 0 ? {...mergedUser, ...nestedPathUpdates} : mergedUser;

      const response = await FirebaseQuery.updateAsync(
        uid,
        `${DB_COLLECTION_NAMES.users}/${uid}`,
        finalUpdates,
        null,
        skipSubscriptionCheck
      );

      return response.data;
    }
  }

  // Helper method to filter out null and undefined values from objects
  filterNullValues(obj: any): any {
    const filtered = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined) {
        if (typeof value === "object" && !Array.isArray(value)) {
          // Recursively filter nested objects
          const filteredNested = this.filterNullValues(value);
          if (Object.keys(filteredNested).length > 0) {
            filtered[key] = filteredNested;
          }
        } else {
          filtered[key] = value;
        }
      }
    }
    return filtered;
  }

  async setUserAsync(uid: string, user: any): Promise<any> {
    user = {ID: uid, ...user};
    if (isAndroid) {
      await (await LocalDbService.getLocalDbService()).setUser(user).catch((err) => {
        logger.error("Can't set user locally : ", err);
      });
    }

    // Subscription stuff are managed by native apps only
    delete user.subscription;
    delete user.subscriptionExpDate;
    delete user.subscriptionExpDateFull;
    delete user.subscriptionType;
    delete user.subscriptionStatus;
    delete user.isFree;

    const response = await FirebaseQuery.setAsync(undefined, `${DB_COLLECTION_NAMES.users}/${uid}`, user, null, true);

    user.uid = uid;
    return response.data;
  }

  async createUserAsync(user: any): Promise<any> {
    const response = await FirebaseQuery.addAsync(undefined, `${DB_COLLECTION_NAMES.users}`, user, true);
    return response.data;
  }

  async getUidAndUserByGarminTokenAsync(garminAccessToken: string, throwError = false): Promise<UidAndUser> {
    /**
     * Get a user depending on the garmin token stores in the user object "{garmin_connect: { oauthAccessToken:value }}"
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("garminConnect/oauthAccessToken"), equalTo(garminAccessToken)],
      true,
      throwError
    );
    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getUidAndUserBySuuntoUsernameAsync(suuntoUsername: string, throwError = false): Promise<UidAndUser> {
    /**
     * Get a user depending on the suunto username stores in the user object "{suunto: { username:value }}"
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("suunto/username"), equalTo(suuntoUsername)],
      true,
      throwError
    );
    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getUidAndUserByStravaAthleteIdAsync(athleteId: number, throwError = false): Promise<UidAndUser> {
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("strava/athleteId"), equalTo(athleteId)],
      true,
      throwError
    );
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getUidAndUserByCorosUsernameAsync(corosUserId: any, throwError = false): Promise<UidAndUser> {
    /**
     * Get a user depending on the coros userId stores in the user object "{coros: { userId:value }}"
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("coros/userId"), equalTo(corosUserId)],
      true,
      throwError
    );
    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getAllUidsAndUsersByGarminTokenAsync(garminAccessToken: string): Promise<any> {
    /**
     * Get users depending on the garmin token stores in the user object
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("garminConnect/oauthAccessToken"), equalTo(garminAccessToken)],
      true
    );

    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return response.data;
    } else {
      return [];
    }
  }

  async getUidAndUserByGarminUserIdAsync(garminUserId: string, throwError = false): Promise<UidAndUser> {
    /**
     * Get a user depending on the persistent Garmin API user id stored in the user object
     * "{garminConnect: {userId: value}}", the identifier Garmin sends in OAuth2 PUSH/PING payloads.
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("garminConnect/userId"), equalTo(garminUserId)],
      true,
      throwError
    );
    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getAllUidsAndUsersByGarminUserIdAsync(garminUserId: string): Promise<any> {
    /**
     * Get every user bound to the same Garmin account (several Waterspeed accounts can share one).
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("garminConnect/userId"), equalTo(garminUserId)],
      true
    );

    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return response.data;
    } else {
      return [];
    }
  }

  async getAllUidsAndUsersWithLegacyGarminTokenAsync(): Promise<Record<string, any>> {
    /**
     * Get every user holding a legacy Garmin OAuth1 access token (the candidates for the OAuth2
     * token exchange). Strings sort after null/booleans/numbers in RTDB, so startAt("") keeps only
     * users whose token is set.
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("garminConnect/oauthAccessToken"), startAt("")],
      true
    );

    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return response.data;
    } else {
      return {};
    }
  }

  async getAllUidsAndUsersOnGarminOAuth2Async(): Promise<Record<string, any>> {
    /**
     * Get every user whose Garmin connection is on OAuth2, connected or exchanged.
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("garminConnect/oauthVersion"), equalTo(2)],
      true
    );

    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return response.data;
    } else {
      return {};
    }
  }

  async getUidAndUserByGlassfyIdAsync(glassfyId: string): Promise<UidAndUser> {
    /**
     * Get a user depending on the glassfy ID in the user object "GF-SubscriberID"
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("GF-SubscriberID"), equalTo(glassfyId)],
      true
    );
    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getUidAndUserByAdaptyIdAsync(adaptyId: string): Promise<UidAndUser> {
    /**
     * Get a user depending on the Adapty ID in the user object "Adapty-ID"
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("Adapty-ID"), equalTo(adaptyId)],
      true
    );
    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getUidAndUserByIcloudIdAsync(iCloudID: string, onlyUser = false): Promise<any> {
    /**
     * Get a user depending on the iCloudID stores in the user object
     */
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("iCloudID"), equalTo(iCloudID)],
      true
    );

    if (onlyUser) {
      if (response.isSuccess) {
        return Object.values(response.data)?.[0];
      } else {
        return null;
      }
    }

    // Response is of the form {data: {uid: user}}
    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getUserByEmail(email: string): Promise<UidAndUser> {
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("email"), equalTo(email)],
      true
    );

    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0], user: Object.values(response.data)[0]};
    } else {
      return {uid: null, user: null};
    }
  }

  async getAllUsersByEmail(email: string): Promise<UidAndUser[]> {
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      `${DB_COLLECTION_NAMES.users}`,
      [orderByChild("email"), equalTo(email)],
      true
    );

    if (response.isSuccess) {
      return Object.keys(response.data).map((uid) => ({uid, user: response.data[uid]}));
    } else {
      return [];
    }
  }

  async getAllUidsAndUsersByIcloudIdAsync(iCloudID: string, currentUser: any, currentUid: string): Promise<any> {
    /**
     * Get a user depending on the iCloudID stores in the user object
     */
    if (iCloudID) {
      const response = await FirebaseQuery.getWithFiltersAsync(
        null,
        `${DB_COLLECTION_NAMES.users}`,
        [orderByChild("iCloudID"), equalTo(iCloudID)],
        true
      );

      if (response.isSuccess) {
        return response.data;
      } else {
        return [];
      }
    } else {
      return {[currentUid]: currentUser};
    }
  }
}

export class AppDataCollection {
  async getAppDataAsync(): Promise<any> {
    const response = await FirebaseQuery.getAsync("", `${DB_COLLECTION_NAMES.appData}`, true);
    return response.data;
  }

  async updateAppDataAsync(appData: any): Promise<void> {
    await FirebaseQuery.updateAsync("", `${DB_COLLECTION_NAMES.appData}`, appData, null, true);
  }
}

export class EquipCollection {
  async getAllEquips(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    limit = 30,
    endAtCond = true,
    lastOffsetKey: any = undefined
  ): Promise<any> {
    const filters = FirebaseQuery.createQuery(offset, search, limit, endAtCond, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(uid, `${DB_COLLECTION_NAMES.equip}`, filters, true);
    return response.data;
  }

  async getAllEquipsByUser(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    limit = 30,
    endAtCond = true,
    lastOffsetKey: any = undefined
  ): Promise<any> {
    const filters = FirebaseQuery.createQuery(offset, search, limit, endAtCond, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(uid, `${DB_COLLECTION_NAMES.equip}/${uid}`, filters, true);
    return response.data;
  }

  async getEquipByIdAsync(uid: string, equipId: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(uid, `${DB_COLLECTION_NAMES.equip}/${uid}/${equipId}`, true);
    return response.data;
  }

  async setEquipAsync(uid: string, equipId: string, equipment: any): Promise<any> {
    equipment = {ID: equipId, ...equipment};

    const response = await FirebaseQuery.setAsync(
      uid,
      `${DB_COLLECTION_NAMES.equip}/${uid}/${equipId}`,
      equipment,
      null,
      true
    );

    equipment.uid = uid;
    return response.data;
  }
}

export class NavCollection {
  async getAllNavs(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    skipSubscriptionCheck = false,
    limit = 30,
    endAtCond = true,
    lastOffsetKey: any = undefined
  ): Promise<any> {
    const filters = FirebaseQuery.createQuery(offset, search, limit, endAtCond, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(
      uid,
      DB_COLLECTION_NAMES.navs,
      filters,
      skipSubscriptionCheck
    );
    return response.data;
  }

  async getAllNavsByUserId(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    limit = 1,
    skipSubscriptionCheck = false,
    lastOffsetKey: any = undefined,
    endAtCond = true
  ): Promise<any> {
    const filters = FirebaseQuery.createQuery(offset, search, limit, endAtCond, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(
      uid,
      `${DB_COLLECTION_NAMES.navs}/${uid}`,
      filters,
      skipSubscriptionCheck
    );
    return response.data;
  }

  async getNavigationsByUserIdAsync(uid: string, skipSubscriptionCheck = false, forceRemote = false): Promise<any> {
    if (isAndroid && !forceRemote) {
      const navsLocal = await (await LocalDbService.getLocalDbService()).getNavigations().catch((err) => {
        LogService.addInfoLogAsync("", `getNavigationsByUserIdAsync error: ${err}`, false, true);
      });

      if (navsLocal) {
        return navsLocal;
      }
    }

    const response = await FirebaseQuery.getAsync(uid, `${DB_COLLECTION_NAMES.navs}/${uid}`, skipSubscriptionCheck);
    return response.data;
  }

  async getNavigationByIdAsync(
    uid: string,
    navId: string,
    skipSubscriptionCheck = false,
    forceRemote = false
  ): Promise<any> {
    if (isAndroid && !forceRemote) {
      const navLocal = await (await LocalDbService.getLocalDbService()).getNavigationById(navId).catch((err) => {
        logger.error("Can't get nav locally : ", err);
      });

      if (navLocal) {
        return navLocal;
      }
    }

    const response = await FirebaseQuery.getAsync(
      uid,
      `${DB_COLLECTION_NAMES.navs}/${uid}/${navId}`,
      skipSubscriptionCheck
    );
    return response.data;
  }

  async getNavByWID(wid: any, uid: string): Promise<any> {
    const widNumber = typeof wid === "string" ? parseInt(wid, 10) : Number(wid);

    const response = await FirebaseQuery.getWithFiltersAsync(
      uid,
      `${DB_COLLECTION_NAMES.navs}/${uid}`,
      [orderByChild("WID"), equalTo(widNumber)],
      true
    );

    if (response.isSuccess) {
      return {uid: Object.keys(response.data)[0]};
    } else {
      return {id: null};
    }
  }

  // `equalTo` is type-sensitive, so each stored form of the id has to be queried in turn.
  private async getNavigationByThirdPartyIdAsync(uid: string, field: string, id: any): Promise<any> {
    for (const candidate of ThirdPartyIdUtil.queryCandidates(id)) {
      const response = await FirebaseQuery.getWithFiltersAsync(
        uid,
        `${DB_COLLECTION_NAMES.navs}/${uid}`,
        [orderByChild(field), equalTo(candidate)],
        true
      );
      if (response.isSuccess && response.data) {
        const match = ThirdPartyIdUtil.pickOriginal(Object.values(response.data));
        if (match) {
          return match;
        }
      }
    }
    return undefined;
  }

  getNavigationByGarminId(uid: string, garminId: any): Promise<any> {
    return this.getNavigationByThirdPartyIdAsync(uid, "gId", garminId);
  }

  getNavigationBySuuntoWorkoutKey(uid: string, suuntoWorkoutKey: any): Promise<any> {
    return this.getNavigationByThirdPartyIdAsync(uid, "sId", suuntoWorkoutKey);
  }

  getNavigationByCorosWorkoutKey(uid: string, corosWorkoutKey: any): Promise<any> {
    return this.getNavigationByThirdPartyIdAsync(uid, "cId", corosWorkoutKey);
  }

  async updateNavigationAsync(
    uid: string,
    navId: string,
    navigation: any,
    onCompleteCallback: ((res?: any) => any) | null = null,
    skipSubscriptionCheck = false
  ): Promise<void> {
    const date = new Date();
    navigation.lmd = date.getTime() / 1000;
    navigation.lmdFull = date.toISOString();
    if (isAndroid) {
      const localDbService = await LocalDbService.getLocalDbService();
      const existingNav = await localDbService.getNavigationById(navId).catch((err) => {
        logger.error("Can't get nav locally before update : ", err);
      });
      await localDbService
        .setNavigation({...(existingNav || {}), ID: navId, ...navigation, saveFirestore: false})
        .catch((err) => {
          logger.error("Can't set nav locally : ", err);
        });
    }

    delete navigation.saveFirestore;
    if (!isAndroid) {
      await FirebaseQuery.updateAsync(
        uid,
        `${DB_COLLECTION_NAMES.navs}/${uid}/${navId}`,
        navigation,
        onCompleteCallback,
        skipSubscriptionCheck
      );
    } else {
      FirebaseQuery.updateAsync(
        uid,
        `${DB_COLLECTION_NAMES.navs}/${uid}/${navId}`,
        navigation,
        onCompleteCallback,
        skipSubscriptionCheck
      );
    }
  }

  async addNavigationAsync(uid: string, navigation: any, key: any = undefined, skipAndroid = false): Promise<any> {
    if (isAndroid && !skipAndroid) {
      const localDbService = await LocalDbService.getLocalDbService();
      await localDbService.setNavigation({...navigation, saveFirestore: false}).catch((err) => {
        logger.error("Can't add nav locally : ", err);
      });
    }

    delete navigation.saveFirestore;
    if (key) {
      return FirebaseQuery.setAsync(uid, `${DB_COLLECTION_NAMES.navs}/${uid}/${key}`, navigation, undefined, true);
    }

    const ref = await FirebaseQuery.addAsync(uid, `${DB_COLLECTION_NAMES.navs}/${uid}`, {}, true);
    await FirebaseQuery.updateAsync(
      uid,
      `${DB_COLLECTION_NAMES.navs}/${uid}/${ref.key}`,
      {
        ID: ref.key,
        idF: ref.key,
        ...navigation
      },
      undefined,
      true
    );
    return ref;
  }

  async addNavigationForCloudSync(uid: string, navigation: any, key: string): Promise<any> {
    delete navigation.saveFirestore;
    if (key) {
      return await FirebaseQuery.setAsync(
        uid,
        `${DB_COLLECTION_NAMES.navs}/${uid}/${key}`,
        navigation,
        undefined,
        true
      );
    }
  }

  async updateSingleKeyNavigationAsync(uid: string, val: any, key: string): Promise<Result<any>> {
    const ref = await FirebaseQuery.updateAsync(uid, `${DB_COLLECTION_NAMES.navs}/${uid}/${key}`, val, undefined, true);
    return ref;
  }

  async deleteNavigationAsync(uid: string, navId: string): Promise<Result<any>> {
    if (isAndroid) {
      const db = await LocalDbService.getLocalDbService();
      await db.deleteNavigationById(navId);
    }

    const response = await FirebaseQuery.removeAsync(uid, `${DB_COLLECTION_NAMES.navs}/${uid}/${navId}`, true);
    return response;
  }

  recalculateNavObjectAfterModify(points: any[], activity: any): any {
    const navObject = Object.create({...activity.activityInDb});

    if (!points || points.length <= 2) {
      return getPausePoints(points);
    }

    let speedMax = 0.0;
    let speedAvg = 0.0;
    let disTot = 0.0;
    let durTot = 0.0;

    let dateStart = 0.0;

    let pauseTot = 0.0;

    let firstPointCoord: any = {
      latitude: 0.0,
      longitude: 0.0
    };

    let lastPointCoord: any = {
      latitude: 0.0,
      longitude: 0.0
    };

    let point;
    let count = 0;
    while (count < points.length) {
      point = points[count];

      if (point.speedInKMH > speedMax) {
        speedMax = point.speedInKMH;
      }

      const pause = Number(point.pauseInSecond);
      if (Number.isFinite(pause)) {
        pauseTot += pause;
      }

      if (count === 0) {
        dateStart = points[count].unixTimestamp;
        firstPointCoord = {latitude: points[count].latitude, longitude: points[count].longitude};
      }

      if (count === points.length - 1) {
        lastPointCoord = {latitude: points[count].latitude, longitude: points[count].longitude};
      }

      if (count > 0) {
        const previousPoint = points[count - 1];
        const currentPoint = points[count];

        durTot += currentPoint.unixTimestamp - previousPoint.unixTimestamp;

        const previousPointCoord = {latitude: previousPoint.latitude, longitude: previousPoint.longitude};
        const currentPointCoord = {latitude: currentPoint.latitude, longitude: currentPoint.longitude};

        disTot += (CoordinateUtil.calculateDistanceBetweenCoordinateInMeter as any)(
          previousPointCoord,
          currentPointCoord,
          currentPoint.accuracy
        );
      }

      count++;
    }

    const disLinear = (CoordinateUtil.calculateDistanceBetweenCoordinateInMeter as any)(
      firstPointCoord,
      lastPointCoord,
      lastPointCoord.accuracy
    );

    if (durTot > 0) {
      speedAvg = (disTot / durTot / 1000) * 3600;
    }

    disTot = disTot / 1000;

    const currentDate = new Date();
    // @ts-ignore -- preserved as-is: Date() invoked without `new` (returns a string at runtime)
    navObject["dSE"] = Date(dateStart * 1000);
    navObject["dS"] = dateStart;
    navObject["dT"] = disTot;
    navObject["dTL"] = disLinear;
    navObject["sA"] = speedAvg;
    navObject["sM"] = speedMax;
    navObject["sT"] = durTot;
    navObject["ps"] = pauseTot;
    navObject["lmd"] = currentDate.getTime() / 1000;
    navObject["lmdFull"] = currentDate.toISOString();
    navObject["bs5"] = calculateBestAvgSpeed(points, 5, 0, SETTING_SPEED_UNITS.km_slash_h)[0];

    return navObject;
  }
}

export class PointCollection {
  async getAllPoints(limit = 1): Promise<any> {
    const response = await FirebaseQuery.getWithFiltersAsync(
      null,
      DB_COLLECTION_NAMES.points,
      [limitToFirst(limit)],
      true,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async getPointsFromFile(uid: string, navId: string): Promise<any[]> {
    const filePath = `Database/Points/${uid}/${navId}.json`;

    let jsonData;
    try {
      const response = await getFromStorage(filePath);
      const text = new TextDecoder().decode(response);
      jsonData = JSON.parse(text);
    } catch (error) {
      // Handle fetch or JSON parsing errors
      logger.warn(
        "Error fetching or parsing points from remote file for uid: " +
          uid +
          " and navId: " +
          navId +
          " trying to get it from remote DB"
      );
      return [];
    }

    // Convert to array (with ids)
    return Object.entries(jsonData).map(([id, value]) => ({
      id,
      ...(value as any)
    }));
  }

  // return an array of points and the source: 0 from local, 1 from remote file, 2 from remote DB
  async getPointsByNavIdAsync(
    uid: string,
    navId: string,
    skipSubscriptionCheck = false,
    forceRemote = false
  ): Promise<[any, number]> {
    if (isAndroid && !forceRemote) {
      const pointsLocal = await (await LocalDbService.getLocalDbService()).getPointsByNavId(navId).catch((err) => {
        logger.error("Can't get points locally : ", err);
      });

      if (pointsLocal && Array.isArray(pointsLocal)) {
        const filteredPoints = pointsLocal.filter(
          (point) => point && point.l !== null && point.l !== 0 && point.n !== null && point.n !== 0
        );
        return [filteredPoints, 0];
      }
    }

    if (!skipSubscriptionCheck) {
      const isUserProResult = await isUserProAsync(uid);
      if (!isUserProResult.isSuccess) {
        return [[], 0];
      }
    }

    const points = await this.getPointsFromFile(uid, navId);

    if (points && points.length > 0) {
      points.forEach((point, index) => {
        point.i = index.toString();
      });
      return [points, 1];
    } else {
      const response = await FirebaseQuery.getAsync(
        uid,
        `${DB_COLLECTION_NAMES.points}/${uid}/${navId}`,
        skipSubscriptionCheck
      );
      return [response.data, 2];
    }
  }

  async getAllPointsByUserId(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    limit = 1,
    skipSubscriptionCheck = false,
    lastOffsetKey: any = undefined
  ): Promise<any> {
    // No need to refactor and use the storage for this one for now because it's only for archiving
    const filters = FirebaseQuery.createQuery(offset, search, limit, true, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(
      uid,
      `${DB_COLLECTION_NAMES.points}/${uid}`,
      filters,
      skipSubscriptionCheck
    );
    return response.data;
  }

  addPointsByNavIdAsync(uid: string, navId: string, points: any[]): Promise<boolean> {
    return this.setPointsByNavIdAsync(uid, navId, points);
  }

  setPointsByNavIdInFirebaseAsync(uid: string, navId: string, points: any): Promise<Result<any>> {
    return FirebaseQuery.setAsync(uid, `${DB_COLLECTION_NAMES.points}/${uid}/${navId}`, points, undefined, true);
  }

  async setPointsByNavIdAsync(uid: string, navId: string, points: any[], forceRemote = false): Promise<boolean> {
    points.forEach((point, index) => {
      point.i = point.i ?? index.toString();
      point.h = point.h ?? 0;
      point.c = point.c ?? 0;
      point.p = point.p ?? 0;
      point.r = point.r ?? "";
    });

    if (isAndroid && !forceRemote) {
      const localDbService = await LocalDbService.getLocalDbService();
      await localDbService.setPointsForNavId(navId, points).catch((err) => {
        (LogService.addErrorLogAsync as any)("", "Can't set points locally :", err, "error", false, true);
      });
    }

    const pointsJson = JSON.stringify(points);

    // Upload the Blob to Firebase Storage
    try {
      if (!isAndroid) {
        await writeToStorage(`Database/Points/${uid}/${navId}.json`, pointsJson);
      } else {
        writeToStorage(`Database/Points/${uid}/${navId}.json`, pointsJson);
      }
      FirebaseQuery.removeAsync(uid, `${DB_COLLECTION_NAMES.points}/${uid}/${navId}`, true);
      return true;
    } catch (error) {
      logger.error("Error uploading points JSON to Firebase Storage:", error);
      try {
        if (!isAndroid) {
          await writeStringToStorage(`Database/Points/${uid}/${navId}.json`, pointsJson);
        } else {
          writeStringToStorage(`Database/Points/${uid}/${navId}.json`, pointsJson);
        }
        FirebaseQuery.removeAsync(uid, `${DB_COLLECTION_NAMES.points}/${uid}/${navId}`, true);
        return true;
      } catch (err) {
        logger.error("Both uploadBytes and uploadString failed:", err);
        return false;
      }
    }
  }

  async removePointsAsync(
    uid: string,
    navId: string,
    pointsToRemove: any[],
    allPoints: any[],
    skipSubscriptionCheck = false
  ): Promise<boolean> {
    try {
      // Create a new array based on allPoints and without the pointsToRemove
      const points = allPoints.filter((point) => {
        return !pointsToRemove.some(
          (pointToRemove) => pointToRemove.id === point.id || pointToRemove.dbKey === point.dbKey
        );
      });

      const pointsDb = points.map((point, index) => convertPointToPointDb(point, index));
      await this.setPointsByNavIdAsync(uid, navId, pointsDb);

      return true;
    } catch (error) {
      LogService.addErrorLogAsync(uid, "error removePointAsync 2" + error.message, error.stack);
      return false;
    }
  }
}

export class LogCollection {
  async removeLogByIdAsync(uid: string, logId: string): Promise<any> {
    if (isAndroid) {
      return null;
    }

    const response = await FirebaseQuery.removeAsync(uid, `${DB_COLLECTION_NAMES.logs}/${uid}/${logId}`, true);
    return response.data;
  }
}

export class SharedDataCollection {
  async getSharedDataByIdAsync(uid: string, sharedDataId: string, skipSubscriptionCheck = false): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      uid,
      `${DB_COLLECTION_NAMES.sharedData}/${sharedDataId}`,
      skipSubscriptionCheck
    );
    return response.data;
  }

  async setSharedDataByIdAsync(uid: string, sharedDataId: string, data: any): Promise<any> {
    const response = await FirebaseQuery.setAsync(
      uid,
      `${DB_COLLECTION_NAMES.sharedData}/${sharedDataId}`,
      data,
      undefined,
      true
    );
    return response.data;
  }
}

export class GarminDataCollection {
  async getAllGarminDataAsync(): Promise<any> {
    const response = await FirebaseQuery.getAsync(null, `${DB_COLLECTION_NAMES.garminData}`, true);
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async getGarminDataByIdAsync(userId: string, activityId: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      null,
      `${DB_COLLECTION_NAMES.garminData}/${userId}/${activityId}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async getGarminDataByTokenAsync(userAccessToken: string): Promise<Record<string, any>> {
    const response = await FirebaseQuery.getAsync(null, `${DB_COLLECTION_NAMES.garminData}/${userAccessToken}`, true);
    return response.isSuccess ? response.data || {} : {};
  }

  deleteGarminDataByIdAsync(username: string, workoutKey: string): Promise<Result<any>> {
    return FirebaseQuery.setAsync(null, `${DB_COLLECTION_NAMES.garminData}/${username}/${workoutKey}`, {}, null, true);
  }

  writeGarminDataAsync(username: string, workoutKey: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.setAsync(
      null,
      `${DB_COLLECTION_NAMES.garminData}/${username}/${workoutKey}`,
      data,
      null,
      true
    );
  }

  updateGarminDataByIdAsync(username: string, workoutKey: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.updateAsync(
      null,
      `${DB_COLLECTION_NAMES.garminData}/${username}/${workoutKey}`,
      data,
      null,
      true
    );
  }
}

export class GarminManualUpdatesCollection {
  async getManualUpdateAsync(userAccessToken: string, activityId: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      null,
      `${DB_COLLECTION_NAMES.garminManualUpdates}/${userAccessToken}/${activityId}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    }
    return null;
  }

  async getAllManualUpdatesByTokenAsync(userAccessToken: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      null,
      `${DB_COLLECTION_NAMES.garminManualUpdates}/${userAccessToken}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    }
    return null;
  }

  writeManualUpdateAsync(userAccessToken: string, activityId: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.setAsync(
      null,
      `${DB_COLLECTION_NAMES.garminManualUpdates}/${userAccessToken}/${activityId}`,
      data,
      null,
      true
    );
  }
}

export class WaitingApprovalActivitiesCollection {
  async getAllWaitingApprovalActivitiesAsync(): Promise<any> {
    const response = await FirebaseQuery.getAsync(null, `${DB_COLLECTION_NAMES.waitingApprovalActivities}`, true);
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async getAllWaitingApprovalActivitiesByUserIdAsync(userId: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      userId,
      `${DB_COLLECTION_NAMES.waitingApprovalActivities}/${userId}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else if ((response.error as any)?.includes("invalid path")) {
      return {};
    } else {
      throw new Error(response.error as any);
    }
  }

  async getWaitingApprovalActivityAsync(userId: string, activityId: string, type: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      userId,
      `${DB_COLLECTION_NAMES.waitingApprovalActivities}/${userId}/${type}/${activityId}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async pushWaitingApprovalActivityAsync(userId: string, activityId: string, type: string, data: any): Promise<any> {
    const response = await FirebaseQuery.setAsync(
      userId,
      `${DB_COLLECTION_NAMES.waitingApprovalActivities}/${userId}/${type}/${activityId}`,
      data,
      undefined,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async removeWaitingApprovalActivityAsync(userId: string, activityId: string, type: string): Promise<any> {
    const response = await FirebaseQuery.removeAsync(
      userId,
      `${DB_COLLECTION_NAMES.waitingApprovalActivities}/${userId}/${type}/${activityId}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }
}

export class SuuntoDataCollection {
  async getAllSuuntoDataAsync(): Promise<any> {
    const response = await FirebaseQuery.getAsync(null, `${DB_COLLECTION_NAMES.suuntoData}`, true);
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async getSuuntoDataByIdAsync(username: string, workoutKey: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      null,
      `${DB_COLLECTION_NAMES.suuntoData}/${username}/${workoutKey}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  deleteSuuntoDataByIdAsync(username: string, workoutKey: string): Promise<Result<any>> {
    return FirebaseQuery.setAsync(null, `${DB_COLLECTION_NAMES.suuntoData}/${username}/${workoutKey}`, {}, null, true);
  }

  writeSuuntoDataAsync(username: string, workoutKey: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.setAsync(
      null,
      `${DB_COLLECTION_NAMES.suuntoData}/${username}/${workoutKey}`,
      data,
      null,
      true
    );
  }

  updateSuuntoDataByIdAsync(username: string, workoutKey: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.updateAsync(
      null,
      `${DB_COLLECTION_NAMES.suuntoData}/${username}/${workoutKey}`,
      data,
      null,
      true
    );
  }
}

export class CorosDataCollection {
  async getAllCorosDataAsync(): Promise<any> {
    const response = await FirebaseQuery.getAsync(null, `${DB_COLLECTION_NAMES.corosData}`, true);
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  async getCorosDataByIdAsync(userId: string, workoutKey: string): Promise<any> {
    const response = await FirebaseQuery.getAsync(
      null,
      `${DB_COLLECTION_NAMES.corosData}/${userId}/${workoutKey}`,
      true
    );
    if (response.isSuccess) {
      return response.data;
    } else {
      throw new Error(response.error as any);
    }
  }

  deleteCorosDataByIdAsync(userId: string, workoutKey: string): Promise<Result<any>> {
    return FirebaseQuery.setAsync(null, `${DB_COLLECTION_NAMES.corosData}/${userId}/${workoutKey}`, {}, null, true);
  }

  writeCorosDataAsync(userId: string, workoutKey: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.setAsync(null, `${DB_COLLECTION_NAMES.corosData}/${userId}/${workoutKey}`, data, null, true);
  }

  updateCorosDataByIdAsync(userId: string, workoutKey: string, data: any): Promise<Result<any>> {
    return FirebaseQuery.updateAsync(
      null,
      `${DB_COLLECTION_NAMES.corosData}/${userId}/${workoutKey}`,
      data,
      null,
      true
    );
  }
}

export class StrokesCollection {
  async getAllStrokesByUserId(
    uid: string,
    offset: any,
    search: SearchFilter | null = null,
    limit = 1,
    skipSubscriptionCheck = false,
    lastOffsetKey: any = undefined
  ): Promise<any> {
    const filters = FirebaseQuery.createQuery(offset, search, limit, true, lastOffsetKey);
    const response = await FirebaseQuery.getWithFiltersAsync(
      uid,
      `${DB_COLLECTION_NAMES.strokes}/${uid}`,
      filters,
      skipSubscriptionCheck
    );
    return response.data;
  }
}
